import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import parse from 'html-react-parser';
import data from '@emoji-mart/data';
import { ReactComponent as SlackIcon } from 'assets/slack-icon.svg';
import { SlackService } from 'service/slack.service';
import zeplinColor from 'theme/Color';
import EmojiPickerPopover from './EmojiPickerPopover';
import SlackReplyBox from './SlackReplyBox';

interface ClientSlackCardProps {
  message: any;
  newDataTotal?: number;
  index?: number;
  isExpanded?: boolean;
  isFirstCard?: boolean;
  total?: number;
  handleCardClick?: () => void;
  onReplyChange?: (open: boolean) => void;
}

const getTimeText = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 1000 / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 23) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
};

const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface Reaction {
  unified?: string;
  native?: string;
  emojiName?: string;
  count: number;
  name?: string; // Optional name property for backward compatibility
}

const getNativeEmojiByName = (emojiName: string): string => {
  const normalizedName = String(emojiName || '')
    .replace(/:/g, '')
    .trim()
    .toLowerCase();

  const emoji = (data as any)?.emojis?.[normalizedName];
  return emoji?.skins?.[0]?.native || '';
};

const normalizeMessageReactions = (incoming: any[] = []): Reaction[] => {
  return incoming
    .map((reaction) => {
      const emojiName = String(reaction?.emojiName || reaction?.name || '')
        .replace(/:/g, '')
        .trim()
        .toLowerCase();
      const native = reaction?.native || getNativeEmojiByName(emojiName);

      return {
        unified: reaction?.unified || emojiName,
        native,
        emojiName,
        count: Number(reaction?.count) || 0,
        name: reaction?.name || emojiName,
      };
    })
    .filter((reaction) => Boolean(reaction?.count) && Boolean(reaction?.unified || reaction?.name));
};

const ClientSlackCard = ({
  message,
  newDataTotal = 0,
  index = 0,
  isExpanded = true,
  isFirstCard = false,
  total = 0,
  handleCardClick = () => {},
  onReplyChange,
}: ClientSlackCardProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [reactions, setReactions] = useState<Reaction[]>(
    normalizeMessageReactions(message?.reactions),
  );
  const [replyOpen, setReplyOpen] = useState(false);

  const getTextLengthThreshold = () => {
    if (isXs) return 20;
    if (isSm) return 25;
    if (isMd) return 28;
    if (isLg) return 35;
    return 58;
  };

  const normalizeEmojiName = (emoji: any): string => {
    const rawName =
      emoji?.id || emoji?.shortcodes || emoji?.shortcode || emoji?.name || emoji?.native || '';

    return String(rawName).replace(/:/g, '').trim().toLowerCase();
  };

  const handleEmojiSelect = async (emoji: any) => {
    const emojiName = normalizeEmojiName(emoji);
    const ts = message?.ts || '';

    if (!channelId || !ts || !emojiName) return;

    try {
      await SlackService.addReaction(channelId, ts, emojiName);
    } catch (err) {
      console.error('Failed to add reaction:', err);
      return;
    }

    setReactions((prev) => {
      const existing = prev.find((r) => r.unified === emoji.unified || r.emojiName === emojiName);
      if (existing) {
        return prev.map((r) =>
          r.unified === emoji.unified || r.emojiName === emojiName
            ? { ...r, count: r.count + 1 }
            : r,
        );
      }
      return [...prev, { unified: emoji.unified, native: emoji.native, emojiName, count: 1 }];
    });
  };

  const handleReactionClick = async (reactionIdentifier: string) => {
    const targetReaction = reactions.find(
      (r) =>
        r.unified === reactionIdentifier ||
        r.name === reactionIdentifier ||
        r.emojiName === reactionIdentifier,
    );
    const ts = message?.ts || '';
    const reactionName = targetReaction?.emojiName || targetReaction?.name;

    if (!channelId || !ts || !reactionName) return;

    try {
      await SlackService.removeReaction(channelId, ts, reactionName);
    } catch (err) {
      console.error('Failed to remove reaction:', err);
      return;
    }

    setReactions((prev) => {
      const existing = prev.find(
        (r) =>
          r.unified === reactionIdentifier ||
          r.name === reactionIdentifier ||
          r.emojiName === reactionIdentifier,
      );
      if (existing && existing.count === 1) {
        return prev.filter(
          (r) =>
            r.unified !== reactionIdentifier &&
            r.name !== reactionIdentifier &&
            r.emojiName !== reactionIdentifier,
        );
      }
      return prev.map((r) =>
        r.unified === reactionIdentifier ||
        r.name === reactionIdentifier ||
        r.emojiName === reactionIdentifier
          ? { ...r, count: r.count - 1 }
          : r,
      );
    });
  };

  const handleReplyToggle = () => {
    const nextOpenState = !replyOpen;

    if (nextOpenState && !isExpanded && total > 1) {
      handleCardClick();
    }

    onReplyChange?.(nextOpenState);
    setReplyOpen(nextOpenState);
  };

  const date =
    message?.date instanceof Date
      ? message.date
      : message?.ts
        ? new Date(parseInt(message.ts.split('.')[0]) * 1000)
        : new Date();

  const timeText = getTimeText(date);
  const isNew = index < newDataTotal || message?.new;
  const senderName = message?.sender || message?.userDetails?.name || 'Unknown';
  const senderImg = message?.sender_img || message?.userDetails?.image;
  const channelName = message?.channel || message?.channelName || '';
  const channelId = message?.channelId || '';
  const threadTs = message?.thread_ts || message?.ts || '';
  const messageText = message?.account || message?.text || '';

  const getCardHeight = () => {
    const textLengthThreshold = getTextLengthThreshold();

    if (isFirstCard) {
      let height = messageText && messageText.length > textLengthThreshold ? 89 : 99;
      if (channelName) height += 35;
      if (total > 1 && !isExpanded) height += 25;
      if (replyOpen) height += 120;
      return `${height}px`;
    }

    if (!isExpanded) {
      let height = messageText && messageText.length > textLengthThreshold ? 89 : 99;
      if (channelName) height += 35;
      if (replyOpen) height += 120;
      return `${height}px`;
    }

    let height = messageText && messageText.length > textLengthThreshold ? 89 : 99;
    if (replyOpen) height += 120;
    return `${height}px`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '12px',
        height: getCardHeight(),
        minHeight: getCardHeight(),
        maxHeight: getCardHeight(),
        border: `1px solid ${replyOpen ? zeplinColor.BasicBlack : zeplinColor.Neutral5}`,
        borderRadius: '12px',
        backgroundColor: isNew ? zeplinColor.Brand1Alpha : zeplinColor.Neutral1,
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        zIndex: 10,
      }}
    >
      {/* Inner row: icon + content */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
        {/* Left — Slack icon */}
        <Box
          sx={{
            borderRadius: '8px',
            border: `1px solid ${zeplinColor.Neutral5}`,
            backgroundColor: zeplinColor.Neutral1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            flexShrink: 0,
          }}
        >
          <SlackIcon width={19} height={19} />
        </Box>

        {/* Right — content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
          {/* Row 1: message + time */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            <Typography
              variant='responsiveBody2'
              sx={{
                flex: 1,
                minWidth: 0,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                lineHeight: 1.4,
              }}
            >
              {parse(messageText)}
            </Typography>
            <Typography
              variant='responsiveBody3'
              color={isNew ? zeplinColor.Brand1 : zeplinColor.Neutral8}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {timeText}
            </Typography>
          </Box>

          {/* Row 2: sender + channel */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {/* Sender */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
              {senderImg ? (
                <Box
                  component='img'
                  src={senderImg}
                  alt={senderName}
                  sx={{ width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0 }}
                />
              ) : (
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: zeplinColor.Brand1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ fontSize: '8px', lineHeight: 1, color: zeplinColor.Neutral1 }}>
                    {getInitials(senderName)}
                  </Typography>
                </Box>
              )}
              <Typography
                variant='responsiveBody3'
                color={zeplinColor.BasicBlack}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {senderName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {/* Channel */}
              {isFirstCard && channelName && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    borderRadius: '50px',
                    backgroundColor: zeplinColor.Neutral3,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src='/assets/slack-channel-lock.svg'
                    style={{ width: '12px', height: '12px' }}
                    alt='channel'
                  />
                  <Typography
                    variant='responsiveBody3'
                    color={zeplinColor.BasicBlack}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {channelName}
                  </Typography>
                </Box>
              )}
              {/* Icons stay in Row 2 only when no reactions */}
              {reactions.length === 0 && (
                <>
                  <img
                    src='/assets/add-emoji.svg'
                    alt='add emoji'
                    style={{ width: '22px', height: '22px', cursor: 'pointer' }}
                    onClick={(e) => setPickerAnchor(e.currentTarget)}
                  />
                  <img
                    src='/assets/open-reply.svg'
                    alt='open reply'
                    style={{ width: '22px', height: '22px', cursor: 'pointer' }}
                    onClick={handleReplyToggle}
                  />
                </>
              )}
            </Box>
          </Box>

          {/* Row 3: reactions + icons (shown once any emoji is added) */}
          {reactions.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
              {reactions.map((r) => (
                <Box
                  key={r.unified || r.name}
                  onClick={() => handleReactionClick(r.unified || r.name || '')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    borderRadius: '50px',
                    border: `1px solid ${zeplinColor.Neutral5}`,
                    backgroundColor: zeplinColor.Neutral3,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: zeplinColor.Neutral4 },
                  }}
                >
                  <span style={{ fontSize: '14px', lineHeight: 1 }}>
                    {r.native || `:${r.name || r.emojiName || ''}:`}
                  </span>
                  <Typography variant='responsiveBody3' color={zeplinColor.Brand1}>
                    {r.count}
                  </Typography>
                </Box>
              ))}
              {/* Icons shift here once reactions exist */}
              <img
                src='/assets/add-emoji.svg'
                alt='add emoji'
                style={{ width: '22px', height: '22px', cursor: 'pointer' }}
                onClick={(e) => setPickerAnchor(e.currentTarget)}
              />
              <img
                src='/assets/open-reply.svg'
                alt='open reply'
                style={{ width: '22px', height: '22px', cursor: 'pointer' }}
                onClick={handleReplyToggle}
              />
            </Box>
          )}

          {isFirstCard && total > 1 && !isExpanded && (
            <Typography
              variant='responsiveBody3'
              color={zeplinColor.Brand1}
              onClick={handleCardClick}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.4,
                cursor: 'pointer',
              }}
            >
              {`${total - 1} more notification${total - 1 > 1 ? 's' : ''}`}
            </Typography>
          )}
        </Box>
      </Box>
      {/* end inner row */}

      {/* Reply box: edge-to-edge, no inner padding */}
      {replyOpen && (
        <Box sx={{ mx: '-12px', mb: '-12px', mt: '8px', flex: 1, minHeight: 0 }}>
          <SlackReplyBox
            onClose={() => handleReplyToggle()}
            channelId={channelId}
            threadTs={threadTs}
          />
        </Box>
      )}

      <EmojiPickerPopover
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        onEmojiSelect={handleEmojiSelect}
      />
    </Box>
  );
};

export default ClientSlackCard;
